<script setup>
import { defineProps, defineEmits, computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import axios from '@/plugins/axios.js';
import KiwishModal from '@/views/components/KiwishModal.vue';
import KiwishHeader from '@/views/components/KiwishHeader.vue';

const router = useRouter();

// Control de scroll indicator
const scrollContainer = ref(null);
const showScrollIndicator = ref(false);

const checkScrollable = () => {
  if (scrollContainer.value) {
    const el = scrollContainer.value;
    showScrollIndicator.value = el.scrollHeight > el.clientHeight && el.scrollTop < el.scrollHeight - el.clientHeight - 20;
  }
};

const handleScroll = () => {
  if (scrollContainer.value) {
    const el = scrollContainer.value;
    showScrollIndicator.value = el.scrollTop < el.scrollHeight - el.clientHeight - 20;
  }
};

onMounted(() => {
  setTimeout(checkScrollable, 100);
  window.addEventListener('resize', checkScrollable);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScrollable);
});

const { t } = useI18n();
const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;

const props = defineProps({
  fan: {
    type: Object,
    default: () => ({})
  },
  fans: {
    type: Array,
    default: () => []
  },
  mode: {
    type: String,
    default: 'new' // 'new' o 'view'
  }
});

// Computed para saber si es modo visualización
const isViewMode = computed(() => props.mode === 'view');

const emit = defineEmits(["done", "markAsRead"]);

// Estado del modal interno
const showHintModal = ref(false);

// Marcar como leído y llamar API
const markAsReadAndCallApi = () => {
  // TagManager event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'view_fan',
  });

  // Llamar a la API para marcar como leído
  if (props.fan?.id) {
    axios.post('/fans/no-newfan', { id: props.fan.id }).catch(() => {});
  } else {
    axios.post('/fans/no-newfan').catch(() => {});
  }

  // Emitir evento para que el padre actualice el estado
  emit("markAsRead", props.fan);
};

// Enviar kiwish: marcar como leído y navegar a crear nuevo secret
const sendKiwish = () => {
  markAsReadAndCallApi();
  emit("done");
  router.push('/app/secrets/new');
};

// Cerrar: marcar como leído y navegar a fans
const closeAndGoToFans = () => {
  markAsReadAndCallApi();
  emit("done");
  router.push('/app/fans');
};

// Abrir el kiwish: mostrar modal con la pista
const openKiwish = () => {
  markAsReadAndCallApi();
  showHintModal.value = true;
};

// Cerrar modal y emitir done para que index.vue cierre NewFan
const closeHintModal = () => {
  showHintModal.value = false;
  emit("done");
};

// Formatear fecha
const formattedDate = computed(() => {
  if (!props.fan?.created_at) return '';
  const date = new Date(props.fan.created_at);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
});

// Obtener la pista
const hint = computed(() => {
  return props.fan?.hint || t('fans.hint.no');
});

// Conteo de fans
const totalFans = computed(() => props.fans?.length || 0);
const unreadFans = computed(() => props.fans?.filter(f => f.new === 1).length || 0);
</script>

<template>
  <div class="new-fan-container" ref="scrollContainer" @scroll="handleScroll">
    <KiwishHeader variant="light" :hide-menu="true" />

    <!-- Contenido principal -->
    <div class="content">
      <!-- Info lines -->
      <p class="info-line" v-if="totalFans == 1">{{ $t('fans.new.received.1') }}</p>
      <p class="info-line" v-if="totalFans != 1">{{ $t('fans.new.received', { count: totalFans }) }}</p>
      <p class="info-line" v-if="unreadFans == 1">{{ $t('fans.new.unread.1') }}</p>
      <p class="info-line" v-if="unreadFans != 1">{{ $t('fans.new.unread', { count: unreadFans }) }}</p>

      <!-- Título -->
      <h1 class="title">{{ isViewMode ? $t('fans.view.title') : $t('fans.new.title') }}</h1>

      <!-- Subtítulo -->
      <p class="subtitle">{{ isViewMode ? $t('fans.view.subtitle') : $t('fans.new.subtitle') }}</p>

      <!-- Descripción -->
      <p class="description">{{ isViewMode ? $t('fans.view.description') : $t('fans.new.description') }}</p>

      <!-- Línea de separación -->
      <div class="separator"></div>

      <!-- Módulo: icono grande a la izquierda, info + botón en medio, cerrar a la derecha -->
      <div class="kiwish-module">
        <div class="kiwish-icon-wrapper">
          <ion-img :src="VUE_ASSETS_URL + 'imgs/icons/icon_kiwish.png'" class="kiwish-icon" />
        </div>
        <div class="kiwish-center">
          <span class="kiwish-date">{{ formattedDate }}</span>
          <p class="kiwish-hint-label">{{ $t('fans.new.hint_label') }}
            <span class="kiwish-hint">{{ hint }}</span>
          </p>
        </div>
        <button class="btn-close-right" @click="closeAndGoToFans">
          {{ $t('close') }}
        </button>
      </div>

      <!-- Línea de separación -->
      <div class="separator separator-bottom"></div>


      <!-- Subtítulo -->
      <p class="title">{{ $t('fans.new.subtitle2') }}</p>

      <!-- Descripción -->
      <p class="description">{{ $t('fans.new.description2') }}</p>

      <!-- Botón enviar kiwish -->
      <div class="btn-container">
        <button class="btn-send-full" @click="sendKiwish">
          {{ $t('fans.new.send_kiwish') }}
        </button>
      </div>
    </div>

    <!-- Indicador de scroll -->
    <div class="scroll-indicator" v-if="showScrollIndicator">
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
      </svg>
    </div>

    <!-- Modal con la pista -->
    <KiwishModal
      :is-open="showHintModal"
      max-width="400px"
      @close="closeHintModal"
    >
      <div class="hint-modal-content">
        <div>
          <ion-text class="s16 d-block">{{ $t('fans.field.hint') }}:</ion-text>
          <ion-text class="r15 d-block mt-2">{{ hint }}</ion-text>
        </div>

        <div class="mt-6">
          <ion-button class="button-pink" expand="block" @click="closeHintModal">{{ $t('close') }}</ion-button>
        </div>
      </div>
    </KiwishModal>
  </div>
</template>

<style scoped>
.new-fan-container {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-flex-direction: column;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--fans-gradient-end);
  background: -webkit-linear-gradient(bottom, var(--fans-gradient-end), var(--fans-gradient-start));
  background: linear-gradient(to top, var(--fans-gradient-end), var(--fans-gradient-start));
  padding: 0 30px;
  padding-top: calc(20px + env(safe-area-inset-top, 0px));
  padding-bottom: calc(30px + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: center;
  padding: 15px 0;
}

.logo-image {
  width: 110px;
  height: auto;
}

.content {
  display: flex;
  flex-direction: column;
}

.info-line {
  font-size: 0.7rem;
  color: #F8E9BF;
  opacity: 0.6;
  margin: 0;
  text-align: right;
  line-height: 1.6;
}

.title {
  font-size: 25px;
  line-height: 34px;
  font-weight: 700;
  color: #F8E9BF;
  margin: 25px 0 10px 0;
  text-align: left;
}

.subtitle {
  font-size: 16px;
  font-weight: 600;
  color: #F8E9BF;
  margin: 0 0 20px 0;
  text-align: left;
}

.description {
  font-size: 16px;
  color: #F8E9BF;
  opacity: 0.75;
  margin: 0 0 0px 0;
  line-height: 1.5;
  text-align: left;
}

.separator {
  height: 1px;
  background: rgba(248, 233, 191, 0.3);
  margin-bottom: 20px;
  margin-top: 20px;
}

.separator-bottom {
  margin-top: 20px;
}

/* Módulo con icono grande a la izquierda */
.kiwish-module {
  display: flex;
  align-items: center;
  gap: 15px;
}

.kiwish-icon-wrapper {
  flex-shrink: 0;
}

.kiwish-icon {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.kiwish-center {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  flex: 1;
}

.kiwish-date {
  font-size: 0.75rem;
  color: #F8E9BF;
  opacity: 0.7;
}

.kiwish-hint-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #F8E9BF;
  margin: 0 0 5px 0;
}

.kiwish-hint {
  font-size: 0.9rem;
  font-weight: 300;
  color: #F8E9BF;
  margin: 0 0 5px 0;
}

.btn-container {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.btn-send-full {
  background: #FF5287;
  border: none;
  border-radius: 10px;
  color: #F8E9BF;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 12px 30px;
  cursor: pointer;
  transition: opacity 0.2s;
  text-align: center;
}

.btn-send-full:active {
  opacity: 0.8;
}

/* Scroll indicator */
.scroll-indicator {
  position: absolute;
  bottom: calc(env(safe-area-inset-bottom, 20px) + 30px);
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  background: rgba(248, 233, 191, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounce 1.5s infinite;
  pointer-events: none;
  z-index: 10;
}

.scroll-indicator svg {
  width: 24px;
  height: 24px;
  color: #F8E9BF;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  40% {
    transform: translateX(-50%) translateY(-8px);
  }
  60% {
    transform: translateX(-50%) translateY(-4px);
  }
}

.btn-close-right {
  background: transparent;
  border: 1px solid #F8E9BF;
  border-radius: 10px;
  color: #F8E9BF;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 10px 15px;
  cursor: pointer;
  transition: opacity 0.2s;
  flex-shrink: 0;
  align-self: center;
}

.btn-close-right:active {
  opacity: 0.8;
}

/* Modal content */
.hint-modal-content {
  text-align: center;
  padding: 10px 0;
}

.button-pink {
  --background: #FF5287 !important;
  --background-activated: #e94a78 !important;
  --background-hover: #ff6b9a !important;
  --color: #FFFFFF !important;
}

.button-pink::part(native) {
  background: #FF5287 !important;
  background-color: #FF5287 !important;
  color: #FFFFFF !important;
}

/* Responsive */
@media (max-height: 700px) {
  .title {
    font-size: 1.3rem;
    margin-top: 15px;
  }
  .description {
    font-size: 0.8rem;
    margin-bottom: 20px;
  }
  .kiwish-icon {
    width: 80px;
    height: 80px;
  }
}

@media (max-height: 600px) {
  .logo-image {
    width: 90px;
  }
  .title {
    font-size: 1.2rem;
    margin-top: 10px;
  }
  .subtitle {
    font-size: 0.85rem;
    margin-bottom: 15px;
  }
  .description {
    font-size: 0.75rem;
    margin-bottom: 15px;
  }
  .kiwish-icon {
    width: 70px;
    height: 70px;
  }
  .kiwish-hint {
    font-size: 0.85rem;
  }
}

/* Hide scrollbar */
*::-webkit-scrollbar {
  display: none !important;
}
</style>

<style>
/* Padding extra para tab bar en apps nativas (no scoped para acceder a .native-app del body) */
.native-app .new-fan-container {
  padding-bottom: calc(30px + env(safe-area-inset-bottom, 0px) + 60px) !important;
}
</style>
